import useMediaQueries from 'hooks/common/useMediaQueries';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Collapse, Grid, IconButton } from '@mui/material';

import { DashboardAnalyticsItem, Maybe } from '@libs/graphql-types';

import DataBlock from '../data-block';
import ByCountryChart from './ByCountryChart';

interface ByCountryProps {
  data: Maybe<DashboardAnalyticsItem>[];
}

interface CountryItemProps {
  country: DashboardAnalyticsItem;
}

const CountryItem = ({ country }: CountryItemProps) => {
  const [expanded, setExpanded] = useState(false);
  const hasStates = country.children && country.children.length > 0;

  const toggleExpanded = () => {
    if (hasStates) {
      setExpanded(!expanded);
    }
  };

  return (
    <>
      <Grid
        justifyContent="space-between"
        alignItems="center"
        item
        container
        onClick={toggleExpanded}
        sx={{
          cursor: hasStates ? 'pointer' : 'default',
          '&:hover': hasStates
            ? { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
            : {},
          borderRadius: 1,
          px: 1,
          py: 0.5,
        }}
      >
        <Grid item container alignItems="center" xs>
          <Grid item>
            {hasStates && (
              <IconButton
                size="small"
                sx={{ mr: 1, p: 0.5 }}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleExpanded();
                }}
              >
                {expanded ? (
                  <ExpandLessIcon fontSize="small" />
                ) : (
                  <ExpandMoreIcon fontSize="small" />
                )}
              </IconButton>
            )}
          </Grid>
          <Grid item sx={{ ml: hasStates ? 0 : 4 }}>
            {country.key}
          </Grid>
        </Grid>
        <Grid item>{country.value}</Grid>
      </Grid>

      {hasStates && (
        <Collapse in={expanded} timeout="auto">
          <Box sx={{ ml: 6, borderLeft: '2px solid #e0e0e0', pl: 2 }}>
            {country.children?.map((state) => (
              <Grid
                justifyContent="space-between"
                key={state?.key}
                item
                container
                sx={{
                  py: 0.25,
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.02)' },
                  borderRadius: 1,
                  px: 1,
                }}
              >
                <Grid item>{state?.key}</Grid>
                <Grid item>{state?.value}</Grid>
              </Grid>
            ))}
          </Box>
        </Collapse>
      )}
    </>
  );
};

const ByCountry = (props: ByCountryProps) => {
  const { t } = useTranslation('dashboard');
  const { isSM } = useMediaQueries();
  const data = ((props?.data as DashboardAnalyticsItem[]) || []).filter(
    (i) => !!i.key,
  );

  // Create chart data using country totals
  const chartData = data.map(({ key, value }) => [key, value]);

  return (
    <DataBlock title={t('v2.communityByCountry')}>
      <Grid spacing={4} direction="column" container>
        {!!chartData.length && (
          <Grid xs={isSM ? 12 : 6} item>
            <ByCountryChart data={chartData} />
          </Grid>
        )}
        <Grid item container direction="column" spacing={1}>
          {data.map((country) => (
            <CountryItem key={country.key} country={country} />
          ))}
        </Grid>
      </Grid>
    </DataBlock>
  );
};

export default ByCountry;
